import { pipe } from 'fp-ts/lib/function';
import * as Hooks from 'preact/hooks';
import * as TrackingActions from '../../store/actions/app';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selectors from '../../store/selectors';
import { useAsyncThrowError } from '../useAsync';
import { initialiseConfigurationFromConfigurator } from '../useInitialiseConfiguration/initialiseConfiguration';
import { useInitSelectionsForConfiguration } from '../useInitialiseConfiguration/useInitSelectionsForConfiguration';
import { useInitialiseInspirationFromRemoveParts } from '../useInitialiseInspirationFromRemoveParts';
import {
    getInspirationProductSelections,
    getMultiPartConfigurators,
} from './getMultiPartConfigurators';
import { useConfiguratorDefinitions } from './useConfiguratorDefinitions';
import { Fixtuur } from '@fixtuur/backpack';

/**
 * The add/remove parts journey allows for the transition from Inspiration to Configuration (add parts) and back (remove parts).
 * This hook supports that journey.
 *
 * Multiple configuration options are available however for now we always select the first one.
 *
 */
export const useAddRemoveParts = (
    resetInitialLoad: () => void,
    currentProduct: Fixtuur.Product.Product | null,
) => {
    const dispatch = useDispatch();

    useInitSelectionsForConfiguration();

    const featureFlags = useSelector(Selectors.Core.featureFlags);
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const configurationStartingProductId = useSelector(
        Selectors.Initialisation.configurationStartingProductId,
    );

    const currentConfiguratorDefinition = useSelector(
        Selectors.Initialisation.currentConfiguratorDefinition,
    );
    const initialiseInspirationFromRemoveParts = useInitialiseInspirationFromRemoveParts();
    const inspirationProductId = currentProduct && Fixtuur.Product.id(currentProduct);
    const inspirationProductType = currentProduct && Fixtuur.Product.productType(currentProduct);
    const inspirationGroupingCode =
        currentProduct && (Fixtuur.Product.groupingCode(currentProduct) ?? null);
    const shouldDisplayUnpublished = useSelector(Selectors.Initialisation.shouldDisplayUnpublished);

    const allConfiguratorDefinitions = useConfiguratorDefinitions();

    /**
     * Filter down the Configurator Definitions to only keep those for which we expect there to be parts
     * available to add.
     */
    const possibleConfiguratorDefinitions = useAsyncThrowError(async () => {
        if (
            serviceConfig &&
            inspirationProductId &&
            allConfiguratorDefinitions &&
            allConfiguratorDefinitions.length > 0 &&
            inspirationProductType
        ) {
            return pipe(
                getInspirationProductSelections(
                    inspirationProductId,
                    inspirationProductType,
                    allConfiguratorDefinitions[0],
                    dispatch,
                ),
                startingSelections =>
                    getMultiPartConfigurators(
                        allConfiguratorDefinitions,
                        serviceConfig,
                        startingSelections,
                        dispatch,
                    ),
            );
        }
        return allConfiguratorDefinitions;
    }, [inspirationProductId, serviceConfig, allConfiguratorDefinitions]).result;

    const showAddParts = Hooks.useMemo(
        () =>
            possibleConfiguratorDefinitions !== undefined &&
            currentConfiguratorDefinition === undefined &&
            possibleConfiguratorDefinitions.length > 0 &&
            featureFlags['add-parts-enabled'] === true,
        [possibleConfiguratorDefinitions, currentConfiguratorDefinition],
    );

    const showRemoveParts = Hooks.useMemo(
        () => currentConfiguratorDefinition && configurationStartingProductId,
        [currentConfiguratorDefinition, configurationStartingProductId],
    );

    const onAddParts = Hooks.useMemo(() => {
        if (!possibleConfiguratorDefinitions) return null;

        const configurator = possibleConfiguratorDefinitions[0];
        if (!configurator) return null;

        return () => {
            resetInitialLoad();
            initialiseConfigurationFromConfigurator(
                configurator,
                true,
                false,
                null,
                null,
                inspirationGroupingCode,
                dispatch,
                shouldDisplayUnpublished,
            );
            dispatch(TrackingActions.addPartsAction());
        };
    }, [possibleConfiguratorDefinitions]);

    const onRemoveParts = () => {
        initialiseInspirationFromRemoveParts(configurationStartingProductId);
        dispatch(TrackingActions.removePartsAction());
    };

    const addRemoveCta = Hooks.useMemo(() => {
        if (showAddParts && onAddParts) {
            return {
                status: 'add' as const,
                onClick: onAddParts,
            };
        } else if (showRemoveParts) {
            return {
                status: 'remove' as const,
                onClick: onRemoveParts,
            };
        } else {
            return undefined;
        }
    }, [showAddParts, showRemoveParts]);

    return {
        addRemoveCta,
        showAddParts,
    };
};
